// Maps Shopify variant option names to Google-supported schema.org property URLs.
// https://developers.google.com/search/docs/appearance/structured-data/product-variants

import { TYPES } from './constants';

const SCHEMA_ORG = 'https://schema.org';

/** Shopify option name (lowercase) → schema.org property URL for variesBy */
export const OPTION_TO_SCHEMA_PROPERTY = {
  color: `${SCHEMA_ORG}/color`,
  colour: `${SCHEMA_ORG}/color`,
  size: `${SCHEMA_ORG}/size`,
  material: `${SCHEMA_ORG}/material`,
  pattern: `${SCHEMA_ORG}/pattern`,
};

/** schema.org property URL → Product field name on variant entities */
export const SCHEMA_PROPERTY_TO_FIELD = {
  [`${SCHEMA_ORG}/color`]: 'color',
  [`${SCHEMA_ORG}/size`]: 'size',
  [`${SCHEMA_ORG}/material`]: 'material',
  [`${SCHEMA_ORG}/pattern`]: 'pattern',
};

/** Options that must never appear in variesBy or additionalProperty */
export const IGNORED_OPTIONS = new Set(['title', 'default title']);

export function normalizeOptionName(name) {
  return name?.toLowerCase().trim() ?? '';
}

export function isIgnoredOption(name) {
  return IGNORED_OPTIONS.has(normalizeOptionName(name));
}

export function getSchemaPropertyUrl(optionName) {
  return OPTION_TO_SCHEMA_PROPERTY[normalizeOptionName(optionName)] ?? null;
}

export function isMappedSchemaOption(optionName) {
  return getSchemaPropertyUrl(optionName) !== null;
}

/**
 * Build variesBy from an option map (option name → Set of values).
 * Only includes Google-supported properties with more than one distinct value.
 */
export function buildVariesBy(optionMap) {
  const urls = new Set();

  for (const [name, values] of Object.entries(optionMap)) {
    if (isIgnoredOption(name) || values.size <= 1) continue;
    const url = getSchemaPropertyUrl(name);
    if (url) urls.add(url);
  }

  return [...urls];
}

/**
 * Extract color, size, material, pattern from selectedOptions using the same
 * name mapping as variesBy (handles Color vs Colour, etc.).
 */
export function getMappedVariantAttributes(selectedOptions = []) {
  const attrs = {};

  for (const opt of selectedOptions) {
    if (isIgnoredOption(opt.name)) continue;

    const url = getSchemaPropertyUrl(opt.name);
    if (!url) continue;

    const field = SCHEMA_PROPERTY_TO_FIELD[url];
    if (field) attrs[field] = opt.value;
  }

  return attrs;
}

/**
 * Custom / unmapped options (e.g. Style) → additionalProperty on variant Product.
 */
export function getAdditionalVariantProperties(selectedOptions = []) {
  return selectedOptions
    .filter((opt) => !isIgnoredOption(opt.name) && !isMappedSchemaOption(opt.name))
    .map((opt) => ({
      '@type': TYPES.property_value,
      name: opt.name,
      value: opt.value,
    }));
}
